import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormEvent, ReactNode } from "react";
import useConfirm, { UseConfirmOptions } from "../../hooks/useConfirm";
import { useTranslation } from "react-i18next";
import OptionsButton, { Option } from "../molecules/OptionsButton";

export interface FormProps {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  disableHeader?: boolean;
  disableHeaderCloseButton?: boolean;
  disableFooter?: boolean;
  disableCancelButton?: boolean;
  disableSubmitButton?: boolean;
  cancelButtonText?: string;
  submitButtonText?: string;
  disableEnter?: boolean;
  noForm?: boolean;
  confirm?: boolean;
  confirmOptions?: UseConfirmOptions;
  onSubmit?: () => void;
  onCancel?: () => void;
  disableSubmit?: boolean;
  options?: Option[];
  loading?: boolean;
  noPadding?: boolean;
}

export default function FormTemplate(props: FormProps) {
  const { t } = useTranslation("FormTemplate");
  const confirm = useConfirm(props.confirmOptions);
  return (
    <Box
      component={props.disableSubmit ? "div" : "form"}
      sx={{
        height: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderRadius: "inherit",
        minWidth: { xs: 300, md: 400 },
      }}
      bgcolor="background.default"
      onSubmit={
        !props.disableSubmit
          ? (e: FormEvent) => {
              e.preventDefault();
              if (props.noForm) e.stopPropagation();
              if (props.loading) return;
              if (props.disableEnter) return;
              if (props.confirm) confirm(() => props.onSubmit?.());
              else props.onSubmit?.();
            }
          : undefined
      }
    >
      {!props.disableHeader && (
        <Box
          p={2}
          py={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="column" spacing={1}>
            <Typography fontWeight={600} variant="h6" color="primary">
              {props.title}
            </Typography>
            {props.subtitle && (
              <Typography fontWeight={400} color="grey">
                {props.subtitle}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={1}>
            {props.options && <OptionsButton options={props.options} />}
            {!props.disableHeaderCloseButton && (
              <IconButton onClick={props.onCancel}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Box>
      )}
      {!props.disableHeader && <Divider />}
      <Box
        p={props.noPadding ? 0 : 2}
        boxSizing="border-box"
        flexGrow={1}
        minHeight={0}
        overflow="auto"
      >
        {props.children}
      </Box>
      <Divider />
      {!props.disableFooter && (
        <Box
          display="flex"
          justifyContent="flex-end"
          bgcolor="background.default"
          sx={{
            borderBottomLeftRadius: "inherit",
            borderBottomRightRadius: "inherit",
          }}
          p={2}
        >
          <Stack direction="row" spacing={2}>
            {!props.disableCancelButton && (
              <Button
                variant="outlined"
                color="error"
                onClick={props.onCancel}
                disabled={props.loading}
              >
                {props.cancelButtonText ?? t("Cancel")}
              </Button>
            )}
            {!props.disableSubmitButton && (
              <Button
                variant="contained"
                type={!props.disableEnter ? "submit" : undefined}
                onClick={() => {
                  if (props.disableEnter) {
                    if (props.confirm) confirm(() => props.onSubmit?.());
                    else props.onSubmit?.();
                  }
                }}
                loading={props.loading}
              >
                {props.submitButtonText ?? t("Submit")}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
